import React, { useState } from 'react';
import { Play } from 'lucide-react';
import AnimatedSection from './AnimatedSection';
import { Button } from './ui/button';

const ProductIntroSection = () => {
  const [selectedImage, setSelectedImage] = useState(0);

  const handleWatchDemo = () => {
    const videoSection = document.getElementById('video-section');
    if (videoSection) {
      videoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const productImages = [
    {
      src: '/images/products/White Cordflex on IV Pole 2 - hand .jpeg',
      alt: 'Cordflex Clip on IV Pole with hand'
    },
    {
      src: '/images/products/LadyInBedCordflex_BothSides.jpeg',
      alt: '2 Cordflex Clips on Bedrails with IV Lines and Cords'
    },
    {
      src: '/images/products/white-cordflex-on-gown-pole.jpg',
      alt: 'Cordflex Clip on Patient Gown Pole'
    },
    {
      src: '/images/products/adapter-bulk.jpeg',
      alt: 'Cordflex Adapter Bulk Cable Management'
    }
  ];

  return (
    <section className="relative py-20 overflow-hidden" style={{ backgroundColor: '#DFEAF0' }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          <defs>
            <pattern id="intro-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="1.5" fill="#214140" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#intro-pattern)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <AnimatedSection animation="fade-up">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#214140' }}>
                Meet the Cordflex Clip
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A simple, intuitive medical line management solution designed by nurses for nurses
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Product Image Gallery */}
              <div className="order-2 lg:order-1">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={productImages[selectedImage].src}
                    alt={productImages[selectedImage].alt}
                    className="w-full h-auto object-cover"
                  />
                </div>

                {/* Thumbnail gallery below main image */}
                <div className="grid grid-cols-4 gap-3 mt-4">
                  {productImages.map((image, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden cursor-pointer shadow transition-all ${
                        selectedImage === index
                          ? 'ring-4 ring-offset-2 scale-105'
                          : 'hover:scale-105'
                      }`}
                      style={{
                        ringColor: selectedImage === index ? '#214140' : 'transparent'
                      }}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Product Description */}
              <div className="order-1 lg:order-2 space-y-6">
                <div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#214140' }}>
                    Simple by Design, Powerful in Practice
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    The Cordflex Clip is a bedside medical line management device that organizes cords, tubes, and cables with the ease of a hair clip. No adhesive, no velcro, no residue—just secure, flexible organization that moves with your patients.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border-l-4" style={{ borderColor: '#8BBAB8' }}>
                  <h4 className="font-bold text-lg mb-3" style={{ color: '#214140' }}>
                    What Makes It Different?
                  </h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#1F8051' }}></div>
                      <span><strong>Clips anywhere:</strong> Bed rails, IV poles, walkers, wheelchairs, patient gowns</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#1F8051' }}></div>
                      <span><strong>Holds everything:</strong> IV lines, suction tubes, EKG leads, ventilator tubing, monitoring cables</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#1F8051' }}></div>
                      <span><strong>Installs in seconds:</strong> Less than 30 seconds to attach and organize multiple lines</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#1F8051' }}></div>
                      <span><strong>Moves with patients:</strong> Portable during transfers, ambulation, imaging, or procedures</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default ProductIntroSection;
